import { useCallback } from 'react';
import { getFrontendConfigLoader } from '@doxyme/config';
import { storage } from '@doxyme/ui/utils';
import { languageOptions } from '@doxyme/ui/config';
import { StorageKeys } from '../../../shared/constants/storageKeys';
import { MediaStorageKeys } from '../../../webrtc/media';
import { getLogger } from '../../../libs/logger';
import { type AuthPayload } from './googleAuthService';
import { getEmailInviteLocalStorageKey } from '../../../components/dashboard/inviteVia/inviteViaEmailForm/formFields/useInvitationField';
import { getTextInviteLocalStorageKey } from '../../../components/dashboard/inviteVia/useInvitationTextBox/invitationTextHelper';
import { languagePreviouslySelectedLocalStorageKey } from '../../../components/dashboard/inviteVia/types';
import { AuthenticationError } from './errors/AuthenticationError';
import { useAuthActions } from '../../../hooks/useAuth';
import { getPublicRuntimeConfig } from '../../../libs/init/getPublicRuntimeConfig';
import { reset as resetAnalytics } from '../../../features/tracking/track';

const logger = getLogger('authService');

const textInviteKeys = languageOptions.map((option) =>
  getTextInviteLocalStorageKey(option.value),
);
const emailInviteKeys = languageOptions.map((option) =>
  getEmailInviteLocalStorageKey(option.value),
);

const storageKeysToKeep = new Set([
  StorageKeys.RememberMe,
  StorageKeys.LocalPreviewExpanded,
  StorageKeys.InviteViaEmailTab,
  StorageKeys.CallHasSeenSidebarPopover,
  StorageKeys.GroupCallGalleryView,
  StorageKeys.PreferredLocale,
  MediaStorageKeys.SelectedDevices,
  languagePreviouslySelectedLocalStorageKey,
  ...textInviteKeys,
  ...emailInviteKeys,
]);

export type ProviderAuthCredentials = {
  userId: string;
  token: string;
};

/**
 * AuthService Logout
 *
 * Use this to log out a provider from doxy.me.
 *
 * Removes all items from localStorage and sessionStorage, except for the rememberMe and SelectedDevices keys.
 * Calls the logout API to remove the session from the DB.
 * Redirects the user to the '/sign-in' URL.
 */
export const logout = async (): Promise<void> => {
  logger.info('[logout] logging user out on logout click');

  await logoutRemotely();
  clearStorage();
  resetAnalytics();
  redirectToSignIn();
};

/**
 * AuthService useLogout
 *
 * Use this to log out a provider from doxy.me.
 *
 * An update to the existing logout function to support the use of Frontegg's logout function.
 */
export const useLogout = (): {
  logout: (redirectUrl?: string) => Promise<void>;
} => {
  const { silentLogout } = useAuthActions();

  const logout = useCallback(
    async (redirectUrl?: string) => {
      logger.info('[logout] logging user out on logout click');

      await logoutRemotely();
      await silentLogout();
      clearStorage();
      resetAnalytics();
      redirectToSignIn(redirectUrl);
    },
    [silentLogout],
  );

  return { logout };
};

/**
 * AuthService handleUnauthorizedRequests
 *
 * Use this to Handle 401 responses from API calls.
 * Redirects the user to the /logout URL, which handles calling the FrontEgg logout hooks.
 */

export const handleUnauthorizedRequests = (): void => {
  const { paths } = getPublicRuntimeConfig();
  const logoutPath = `${paths.base}${paths.logout}`;

  if (window.location.pathname !== logoutPath) {
    logger.info('[handleUnauthorizedRequests] redirecting to logout page');

    location.assign(`${logoutPath}?r=${location.pathname + location.search}`);
  }
};

export const storeAuthCredentials = (payload: AuthPayload): void => {
  const { id: accessTokenId, userId } = payload;

  if (!Boolean(accessTokenId) || !Boolean(userId)) {
    throw new Error('Invalid authentication response: missing required fields');
  }

  storage.setItem(StorageKeys.AccessTokenId, accessTokenId);
  storage.setItem(StorageKeys.CurrentUserId, userId);
  storage.setItem(StorageKeys.RememberMe, true);
};

/**
 * Makes a call to the logout API to remove the user access token from the DB.
 */
export async function logoutRemotely(): Promise<void> {
  const ogToken = storage.findItem(StorageKeys.AccessTokenId) as string;

  // If no auth header, skip remote logout since we're already logged out
  if (!ogToken) {
    logger.info('[logout] No auth header present, skipping remote logout');
    return;
  }

  const baseUrl = getFrontendConfigLoader().get('API_URL') as string;
  await fetch(`${baseUrl}/users/logout/`, {
    method: 'post',
    headers: {
      authorization: ogToken,
    },
  }).catch((...args) => logger.error('[logout] Failed to log out', args));
}

type UserResponse = {
  id: string;
  userId: string;
};

type AuthErrorResponse = {
  error: {
    code: string;
    status: number;
    message: string;
  };
};

export const loginV1 = async (
  email: string,
  password: string,
  institutionId: number | undefined,
  rememberMe: boolean,
): Promise<void> => {
  const baseUrl = getFrontendConfigLoader().get('API_URL') as string;
  const loginUrl = `${baseUrl}/users/login?include=user&rememberMe=false`;

  try {
    const response = await fetch(loginUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
        institutionId,
      }),
    });

    const data = await response.json();
    switch (response.status) {
      case 200: {
        const userData = data as UserResponse;
        if (!userData.id || !userData.userId) {
          throw new Error('Invalid user data received');
        }

        storage.setItem(StorageKeys.AccessTokenId, userData.id, rememberMe);
        storage.setItem(StorageKeys.CurrentUserId, userData.userId, rememberMe);
        return;
      }
      case 401: {
        const errorData = data as AuthErrorResponse;
        throw new AuthenticationError(
          errorData.error?.message ??
            'Authentication failed with wrong username or password',
        );
      }
      default:
        throw new Error(
          `Authentication failed with status: ${response.status}`,
        );
    }
  } catch (error) {
    if (error instanceof AuthenticationError) {
      throw error;
    }

    logger.error('[login] Authentication failed', {
      error,
    });

    throw new Error('Login failed. Please try again later.');
  }
};

export const redirectToSignIn = (redirectUrl?: string): void => {
  if (document.location.pathname.includes('/sign-in')) {
    return;
  }

  const v2SignIn = '/v2';

  logger.debug(`[redirectToSignIn] Path: ${v2SignIn}/sign-in`);

  const referrer = `?r=${redirectUrl ?? location.pathname + location.search}`;

  location.assign(`${v2SignIn}/sign-in${referrer}`);
};

export const resetPassword = async (
  password: string,
  accessToken: string,
): Promise<void> => {
  const baseUrl = getFrontendConfigLoader().get('API_URL') as string;

  try {
    const response = await fetch(`${baseUrl}/users/newPassword`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accessToken,
        password,
      }),
    });

    if (!response.ok) {
      throw new Error('Error during password reset request');
    }
  } catch (error) {
    logger.error('[resetPassword]', error);
    throw error;
  }
};

/**
 * On logout we need to remove user data from local and session storage.
 */
export function clearStorage(): void {
  // VirtualBackgroundEffect key shouldn't be deleted.
  for (const key of Object.keys(sessionStorage)) {
    if (
      !storageKeysToKeep.has(key as StorageKeys | MediaStorageKeys) &&
      !key.startsWith(StorageKeys.VirtualBackgroundEffect)
    ) {
      sessionStorage.removeItem(key);
    }
  }
  for (const key of Object.keys(localStorage)) {
    if (
      !storageKeysToKeep.has(key as StorageKeys | MediaStorageKeys) &&
      !key.startsWith(StorageKeys.VirtualBackgroundEffect)
    ) {
      localStorage.removeItem(key);
    }
  }
}

export const verifyToken = async (accessToken: string): Promise<boolean> => {
  const baseUrl = getFrontendConfigLoader().get('API_URL') as string;

  try {
    const response = await fetch(`${baseUrl}/users/verifyToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ accessToken }),
    });

    return response.ok;
  } catch (error) {
    logger.error(
      '[verifyToken] Error during token verification request',
      error,
    );
    throw error;
  }
};
